import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useAuth } from "../../Common/Configurations/AuthContext";
import { useNavigate } from "react-router-dom";
import "./Dashboard.css";
import apiService from "../../Common/Configurations/APIService";
import config from "../../Common/Configurations/APIConfig";
import BookingItem from "./BookingItem";
import {
  FaPlane,
  FaHotel,
  FaUmbrellaBeach,
  FaUsers,
  FaBuilding,
  FaSyncAlt,
  FaEye,
  FaCalendarAlt,
  FaHandshake,
  FaCheckCircle,
  FaUserFriends,
  FaPlusCircle,
  FaTimesCircle,
  FaLock,
  FaBan,
  FaPassport,
  FaCog,
} from "react-icons/fa";
import { Line, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ArcElement,
} from "chart.js";
import Header from "../../Common/Layout/Header";
import moment from "moment";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ArcElement
);

const initialDashboardData = {
  totalBranches: 0,
  branches: [],
  counts: {
    flights: {},
    hotels: {},
    visas: {},
    holidays: {},
    leads: {},
    customers: {},
  },
  recentFlights: [],
  recentHotels: [],
  recentVisas: [],
  leadOverview: {
    sourceCounts: [],
    monthlyTrend: [],
    conversionBySource: [],
    leadStatusCounts: [],
    totalLeads: 0,
    dateRange: {
      fromDate: "",
      toDate: "",
    },
  },
};

// Status colors configuration
const statusColors = {
  NEW: {
    background: "linear-gradient(135deg, #4361ee, #3a56d4)",
    chart: "#4361ee",
    icon: "#ffffff",
  },
  PROCESSING: {
    background: "linear-gradient(135deg, #9c27b0, #7b1fa2)",
    chart: "#9c27b0",
    icon: "#ffffff",
  },
  "NOT RESPONDING": {
    background: "linear-gradient(135deg, #ef476f, #e03c5f)",
    chart: "#ef476f",
    icon: "#ffffff",
  },
  "FOLLOW UP": {
    background: "linear-gradient(135deg, #f9c74f, #f4b400)",
    chart: "#f9c74f",
    icon: "#ffffff",
  },
  CLOSED: {
    background: "linear-gradient(135deg, #dc2626, #b91c1c)",
    chart: "#dc2626",
    icon: "#ffffff",
  },
  REPLIED: {
    background: "linear-gradient(135deg, #10b981, #0da271)",
    chart: "#10b981",
    icon: "#ffffff",
  },
  "NOT WORKING": {
    background: "linear-gradient(135deg, #6c757d, #5a6268)",
    chart: "#6c757d",
    icon: "#ffffff",
  },
};

// Status icons components
const PlusCircleIcon = () => <FaPlusCircle />;
const CheckCircleIcon = () => <FaCheckCircle />;
const TimesCircleIcon = () => <FaTimesCircle />;
const SyncIcon = () => <FaSyncAlt />;
const LockIcon = () => <FaLock />;
const BanIcon = () => <FaBan />;
const ProcessingIcon = () => <FaCog />;

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [dashboardData, setDashboardData] = useState(initialDashboardData);
  const [leadDashboardData, setLeadDashboardData] = useState({
    sourceCounts: [],
    monthlyTrend: [],
    conversionBySource: [],
    leadStatusCounts: [],
    totalLeads: 0,
    dateRange: {
      fromDate: "",
      toDate: "",
    },
  });
  const [loading, setLoading] = useState(true);
  const [leadLoading, setLeadLoading] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState("all");
  const [timeFilter, setTimeFilter] = useState("this_month");
  const [activeBookingTab, setActiveBookingTab] = useState("flight");
  const [distributionType, setDistributionType] = useState("services");

  const getDashboardData = useCallback(async () => {
    if (!user?.cid) return;
    setLoading(true);
    try {
      let resourceUrl = `${config.GETDASHBOARD}/${user.cid}?dateRange=${timeFilter}`;
      if (selectedBranch !== "all") {
        resourceUrl += `&branchId=${selectedBranch}`;
      }
      const response = await apiService.getAll(resourceUrl);
      setDashboardData(response || initialDashboardData);
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
      setDashboardData(initialDashboardData);
    } finally {
      setLoading(false);
    }
  }, [user, selectedBranch, timeFilter]);

  // Fetch lead dashboard data using the same API as LeadManagementDashboard
  const getLeadDashboardData = useCallback(async () => {
    if (!user?.id) return;
    
    setLeadLoading(true);
    try {
      // Set date range to current month
      const today = new Date();
      const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const lastDayOfMonth = new Date(
        today.getFullYear(),
        today.getMonth() + 1,
        0
      );

      const fromDateStr = moment(firstDayOfMonth).format("YYYY-MM-DD");
      const toDateStr = moment(lastDayOfMonth).format("YYYY-MM-DD");

      // Build query parameters
      const params = new URLSearchParams();
      params.append("fromDate", fromDateStr);
      params.append("toDate", toDateStr);

      // Use the user-based API endpoint
      const url = `${config.GETLEADDASHBOARDBYUSER}/${user.id}?${params}`;
      const response = await apiService.getAll(url);

      setLeadDashboardData({
        sourceCounts: response?.sourceCounts || [],
        monthlyTrend: response?.monthlyTrend || [],
        conversionBySource: response?.conversionBySource || [],
        leadStatusCounts: response?.leadStatusCounts || [],
        totalLeads: response?.totalLeads || 0,
        dateRange: response?.dateRange || {
          fromDate: fromDateStr,
          toDate: toDateStr,
        },
      });
    } catch (error) {
      console.error("Error fetching lead dashboard data:", error);
      setLeadDashboardData({
        sourceCounts: [],
        monthlyTrend: [],
        conversionBySource: [],
        leadStatusCounts: [],
        totalLeads: 0,
        dateRange: {
          fromDate: "",
          toDate: "",
        },
      });
    } finally {
      setLeadLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!authLoading && user) {
      getDashboardData();
      getLeadDashboardData();
    } else if (!authLoading && !user) {
      navigate("/");
    }
  }, [authLoading, user, navigate, getDashboardData, getLeadDashboardData]);

  // Memoize the list of recent bookings to filter efficiently
  const visibleBookings = useMemo(() => {
    const flights = dashboardData.recentFlights || [];
    const hotels = dashboardData.recentHotels || [];
    const visas = dashboardData.recentVisas || [];
    const allBookings = [...flights, ...hotels, ...visas].sort(
      (a, b) => new Date(b.bookingDate) - new Date(a.bookingDate)
    );

    if (activeBookingTab === "all") return allBookings;
    return allBookings.filter(
      (b) => b.bookingType.toLowerCase() === activeBookingTab
    );
  }, [dashboardData, activeBookingTab]);

  // Get status icon based on status name
  const getStatusIcon = (statusName) => {
    switch (statusName) {
      case "NEW":
        return <PlusCircleIcon />;
      case "PROCESSING":
        return <ProcessingIcon />;
      case "NOT RESPONDING":
        return <TimesCircleIcon />;
      case "FOLLOW UP":
        return <SyncIcon />;
      case "CLOSED":
        return <LockIcon />;
      case "REPLIED":
        return <CheckCircleIcon />;
      case "NOT WORKING":
        return <BanIcon />;
      case "NOT RESPONDING/NOT WORKING":
        return <TimesCircleIcon />;
      default:
        return <PlusCircleIcon />;
    }
  };

  // Static chart data
  const chartConfig = {
    line: {
      data: {
        labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul"],
        datasets: [
          {
            label: "Bookings",
            data: [65, 59, 80, 81, 56, 55, 90],
            borderColor: "hsl(221, 83%, 53%)",
            backgroundColor: "hsla(221, 83%, 53%, 0.1)",
            fill: true,
            tension: 0.4,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          y: { beginAtZero: true, grid: { drawBorder: false } },
          x: { grid: { display: false } },
        },
      },
    },
    doughnut: {
      data: {
        services: {
          labels: ["Flights", "Hotels", "Visas", "Holidays"],
          datasets: [
            {
              data: [40, 25, 20, 15],
              backgroundColor: ["#3b82f6", "#8b5cf6", "#f59e0b", "#10b981"],
              borderColor: "#ffffff",
              borderWidth: 4,
            },
          ],
        },
        branches: {
          labels: ["Mumbai", "Delhi", "Bangalore", "Pune"],
          datasets: [
            {
              data: [40, 25, 20, 15],
              backgroundColor: ["#3b82f6", "#ef4444", "#10b981", "#f97316"],
              borderColor: "#ffffff",
              borderWidth: 4,
            },
          ],
        },
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: "70%",
        plugins: {
          legend: {
            display: true,
            position: "bottom",
            labels: {
              usePointStyle: true,
              pointStyle: "circle",
              padding: 20,
              boxWidth: 8,
              font: { size: 14 },
            },
          },
        },
      },
    },
  };

  if (authLoading) return <p className="loading-fullscreen">Loading User...</p>;

  const counts = dashboardData.counts;

  return (
    <div id="layout-wrapper" className="dashboard-body">
      <Header User={user} activeSection="dashboard" />
      <div className="main-content">
        <div className="page-content">
          <div className="dashboard-container">
            <div className="dashboard-filters">
              <div className="welcome-text">
                <h2>Welcome back, {user?.brandName || "Admin"}! </h2>
                <p>Here is the pulse of your business operations.</p>
              </div>
              <div className="filter-group">
                <label htmlFor="branch-filter">
                  <FaBuilding />
                </label>
                <select
                  id="branch-filter"
                  value={selectedBranch}
                  onChange={(e) => setSelectedBranch(e.target.value)}
                  disabled={loading}
                >
                  <option value="all">All Branches</option>
                  {(dashboardData.branches || []).map((branch) => (
                    <option key={branch.branchId} value={branch.branchId}>
                      {branch.branchName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label htmlFor="time-filter">
                  <FaCalendarAlt />
                </label>
                <select
                  id="time-filter"
                  value={timeFilter}
                  onChange={(e) => setTimeFilter(e.target.value)}
                  disabled={loading}
                >
                  <option value="this_month">This Month</option>
                  <option value="today">Today</option>
                  <option value="this_week">This Week</option>
                  <option value="last_month">Last Month</option>
                </select>
              </div>
              <button
                className="btn-primary refresh-btn"
                onClick={() => {
                  getDashboardData();
                  getLeadDashboardData();
                }}
                disabled={loading || leadLoading}
              >
                <FaSyncAlt className={loading || leadLoading ? "spinner" : ""} />
                <span>{loading || leadLoading ? "Loading..." : "Refresh"}</span>
              </button>
            </div>

            {/* Lead Overview Section - Using leadDashboardData from API */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="card bg-white">
                  <div className="card-body bg-light">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="card-title mb-0 text-dark">
                        Lead Overview
                      </h5>
                      <div className="d-flex gap-3">
                        <span className="badge bg-primary fs-6">
                          Total Leads: {leadDashboardData.totalLeads || 0}
                        </span>
                        <span className="badge bg-success fs-6">
                          Converted:{" "}
                          {leadDashboardData.conversionBySource?.reduce(
                            (total, source) => total + source.convertedLeads,
                            0
                          ) || 0}
                        </span>
                      </div>
                    </div>

                    {/* Date Range Info */}
                    {leadDashboardData.dateRange?.fromDate && (
                      <div className="row mb-3">
                        <div className="col-12">
                          <small className="text-muted">
                            Showing data from{" "}
                            {moment(
                              leadDashboardData.dateRange.fromDate
                            ).format("DD MMM YYYY")}{" "}
                            to{" "}
                            {moment(
                              leadDashboardData.dateRange.toDate
                            ).format("DD MMM YYYY")}
                          </small>
                        </div>
                      </div>
                    )}

                    {/* Status Cards - Combined NOT RESPONDING and NOT WORKING */}
                    {leadLoading ? (
                      <div className="text-center py-4">
                        Loading lead status data...
                      </div>
                    ) : (
                      <div className="row">
                        {/* Define the fixed order of statuses - COMBINED NOT RESPONDING and NOT WORKING */}
                        {[
                          "NEW",
                          "PROCESSING",
                          "FOLLOW UP",
                          "REPLIED",
                          "NOT RESPONDING/NOT WORKING", // Combined status
                          "CLOSED",
                        ].map((statusName) => {
                          // Handle combined status
                          let count = 0;
                          let statusConfig;
                          
                          if (statusName === "NOT RESPONDING/NOT WORKING") {
                            // Sum up counts from both NOT RESPONDING and NOT WORKING
                            const notRespondingData =
                              leadDashboardData.leadStatusCounts?.find(
                                (status) => status.leadStatusName === "NOT RESPONDING"
                              );
                            const notWorkingData =
                              leadDashboardData.leadStatusCounts?.find(
                                (status) => status.leadStatusName === "NOT WORKING"
                              );

                            count =
                              (notRespondingData?.count || 0) +
                              (notWorkingData?.count || 0);
                            statusConfig = statusColors["NOT RESPONDING"]; // Use NOT RESPONDING color
                          } else {
                            // Find the status data from the API response for individual statuses
                            const statusData =
                              leadDashboardData.leadStatusCounts?.find(
                                (status) => status.leadStatusName === statusName
                              ) || { leadStatusName: statusName, count: 0 };

                            count = statusData.count || 0;
                            statusConfig =
                              statusColors[statusName] || statusColors["NEW"];
                          }

                          const icon = getStatusIcon(statusName);

                          // For the combined card, use a special display name
                          const displayName =
                            statusName === "NOT RESPONDING/NOT WORKING"
                              ? "NOT RESPONDING/NOT WORKING"
                              : statusName;

                          return (
                            <div
                              key={statusName}
                              className="col-md-4 col-lg-2 mb-3"
                            >
                              <div
                                className="card text-white border-0 shadow-sm h-100 lead-status-card"
                                style={{
                                  background: statusConfig.background,
                                  borderRadius: "15px",
                                  overflow: "hidden",
                                }}
                              >
                                <div className="card-body d-flex justify-content-between align-items-center p-3">
                                  {/* Left Side */}
                                  <div className="text-start">
                                    <p className="mb-1 fw-semibold text-uppercase small text-white">
                                      {displayName}
                                    </p>
                                    <h2 className="mb-0 fw-bold text-white lead-status-count">
                                      {count}
                                    </h2>
                                  </div>

                                  {/* Right Side Icon */}
                                  <div className="status-icon fs-1">{icon}</div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="summary-grid">
              <div className="summary-card theme-flights">
                <div className="card-header">
                  <div className="metric-icon flights">
                    <FaPlane />
                  </div>
                  <h3>Flights</h3>
                </div>
                <div className="card-stats">
                  <div className="stat-item">
                    <span>Upcoming Departures</span>
                    <span className="value">
                      {loading ? "..." : counts.flights.upcomingDepartures || 0}
                    </span>
                  </div>
                  <div className="stat-item">
                    <span>Confirmed Bookings</span>
                    <span className="value">
                      {loading ? "..." : counts.flights.confirmedBookings || 0}
                    </span>
                  </div>
                  <div className="stat-item">
                    <span>Unconfirmed</span>
                    <span className="value">
                      {loading
                        ? "..."
                        : counts.flights.unconfirmedBookings || 0}
                    </span>
                  </div>
                  <div className="stat-item">
                    <span>Cancelled</span>
                    <span className="value">
                      {loading ? "..." : counts.flights.cancelledBookings || 0}
                    </span>
                  </div>
                </div>
                <div className="card-footer">
                  <button
                    className="view-btn"
                    onClick={() => navigate("/flight/bookings")}
                  >
                    <FaEye /> View Details
                  </button>
                </div>
              </div>

              <div className="summary-card theme-hotels">
                <div className="card-header">
                  <div className="metric-icon hotels">
                    <FaHotel />
                  </div>
                  <h3>Hotels</h3>
                </div>
                <div className="card-stats">
                  <div className="stat-item">
                    <span>Upcoming Check-ins</span>
                    <span className="value">
                      {loading ? "..." : counts.hotels.upcomingCheckIns || 0}
                    </span>
                  </div>
                  <div className="stat-item">
                    <span>Confirmed Bookings</span>
                    <span className="value">
                      {loading ? "..." : counts.hotels.confirmedBookings || 0}
                    </span>
                  </div>
                  <div className="stat-item">
                    <span>Pending Confirmation</span>
                    <span className="value">
                      {loading ? "..." : counts.hotels.pendingConfirmation || 0}
                    </span>
                  </div>
                  <div className="stat-item">
                    <span>Cancelled</span>
                    <span className="value">
                      {loading ? "..." : counts.hotels.cancelledBookings || 0}
                    </span>
                  </div>
                </div>
                <div className="card-footer">
                  <button
                    className="view-btn"
                    onClick={() => navigate("/hotel/bookings")}
                  >
                    <FaEye /> View Details
                  </button>
                </div>
              </div>

              <div className="summary-card theme-visas">
                <div className="card-header">
                  <div className="metric-icon visas">
                    <FaPassport />
                  </div>
                  <h3>Visas</h3>
                </div>
                <div className="card-stats">
                  <div className="stat-item">
                    <span>Total Requests</span>
                    <span className="value">
                      {loading ? "..." : counts.visas.totalRequests || 0}
                    </span>
                  </div>
                  <div className="stat-item">
                    <span>Approved</span>
                    <span className="value green">
                      {loading ? "..." : counts.visas.approved || 0}
                    </span>
                  </div>
                  <div className="stat-item">
                    <span>Pending</span>
                    <span className="value warm">
                      {loading ? "..." : counts.visas.pending || 0}
                    </span>
                  </div>
                  <div className="stat-item">
                    <span>In Progress</span>
                    <span className="value">
                      {loading ? "..." : counts.visas.inProgress || 0}
                    </span>
                  </div>
                </div>
                <div className="card-footer">
                  <button
                    className="view-btn"
                    onClick={() => navigate("/requests")}
                  >
                    <FaEye /> View Details
                  </button>
                </div>
              </div>

              <div className="summary-card theme-packages">
                <div className="card-header">
                  <div className="metric-icon packages">
                    <FaUmbrellaBeach />
                  </div>
                  <h3>Holidays</h3>
                </div>
                <div className="card-stats">
                  <div className="stat-item">
                    <span>Total Packages</span>
                    <span className="value">
                      {loading ? "..." : counts.holidays.totalPackages || 0}
                    </span>
                  </div>
                  <div className="stat-item">
                    <span>Active Packages</span>
                    <span className="value">
                      {loading ? "..." : counts.holidays.activePackages || 0}
                    </span>
                  </div>
                  <div className="stat-item">
                    <span>InActive Packages</span>
                    <span className="value">
                      {loading ? "..." : counts.holidays.inActivePackages || 0}
                    </span>
                  </div>
                  <div className="stat-item">
                    <span>Total Enquiries</span>
                    <span className="value">
                      {loading ? "..." : counts.holidays.totalEnquiries || 0}
                    </span>
                  </div>
                </div>
                <div className="card-footer">
                  <button
                    className="view-btn"
                    onClick={() => navigate("/packageslist")}
                  >
                    <FaEye /> View Packages
                  </button>
                </div>
              </div>
            </div>

            <div className="main-content-grid">
              <div className="glass-card recent-bookings-new">
                <div className="card-header">
                  <h3>Recent Bookings</h3>
                  <div className="tabs-container service-tabs">
                    <button
                      className={`tab ${
                        activeBookingTab === "flight" ? "active" : ""
                      }`}
                      onClick={() => setActiveBookingTab("flight")}
                    >
                      <FaPlane /> Flights
                    </button>
                    <button
                      className={`tab ${
                        activeBookingTab === "hotel" ? "active" : ""
                      }`}
                      onClick={() => setActiveBookingTab("hotel")}
                    >
                      <FaHotel /> Hotels
                    </button>
                    <button
                      className={`tab ${
                        activeBookingTab === "visa" ? "active" : ""
                      }`}
                      onClick={() => setActiveBookingTab("visa")}
                    >
                      <FaPassport /> Visas
                    </button>
                    <button
                      className={`tab ${
                        activeBookingTab === "all" ? "active" : ""
                      }`}
                      onClick={() => setActiveBookingTab("all")}
                    >
                      All
                    </button>
                  </div>
                </div>
                <div className="new-bookings-list">
                  {loading ? (
                    <p className="loading-placeholder">Loading bookings...</p>
                  ) : visibleBookings.length > 0 ? (
                    visibleBookings.map((booking) => (
                      <BookingItem
                        key={`${booking.bookingType}-${booking.bookingId}`}
                        booking={booking}
                      />
                    ))
                  ) : (
                    <p className="loading-placeholder">
                      No recent bookings found.
                    </p>
                  )}
                </div>
              </div>

              <div className="charts-container">
                <div className="glass-card booking-distribution">
                  <div className="card-header">
                    <h3>Booking Distribution</h3>
                    <div className="toggle-buttons">
                      <button
                        className={
                          distributionType === "services" ? "active" : ""
                        }
                        onClick={() => setDistributionType("services")}
                      >
                        Services
                      </button>
                      <button
                        className={
                          distributionType === "branches" ? "active" : ""
                        }
                        onClick={() => setDistributionType("branches")}
                      >
                        Branches
                      </button>
                    </div>
                  </div>
                  <div className="doughnut-chart-container">
                    <Doughnut
                      data={chartConfig.doughnut.data[distributionType]}
                      options={chartConfig.doughnut.options}
                    />
                  </div>
                </div>
                <div className="glass-card booking-trends">
                  <div className="card-header">
                    <h3>Booking Trends</h3>
                    <span className="subtitle">Last 7 Months</span>
                  </div>
                  <div className="line-chart-container">
                    <Line
                      data={chartConfig.line.data}
                      options={chartConfig.line.options}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;